import { useCallback, useEffect, useRef } from "react";
import {
  fetchCoAuthorsByPaperId,
  fetchPaperCount,
  fetchPaperFileMetadata,
  fetchPapersByRegistrationId,
  fetchUserRegistrations,
} from "../conferenceRegistrationApi";
import { hasSuccessfulPaymentRecord } from "../paymentCalculations";
import {
  isPostPaymentAbstractEnabled,
  POST_PAYMENT_ABSTRACT_FORM_STEP,
} from "../registrationModules";

export const useExistingRegistrationCheck = ({
  existingUser,
  conferenceName,
  registrationBaseUrl,
  conferenceId,
  accompanyingPersonFee,
  setFormData,
  setStep,
  conferenceDataLoadedRef,
  registrationCheckRequestIdRef,
  setHasPaperSubmitted,
  setPaperApprovalStatus,
  setSubmittedPapers,
  setCategoriesHistory,
  setRegistrationTypesHistory,
  setInitialPaidCategoryId,
  setInitialPaidRegistrationTypeId,
  setInitialPaidCategoryValue,
  setInitialPaidRegistrationTypeValue,
  setExistingPaperId,
  setIsPaperLocked,
  setPaperTitle,
  setAbstractText,
  setPresentationMode,
  setSubmissionMethod,
  setSelectedTrack,
  setWantsToSubmitAbstract,
  setPaperFileUploaded,
  setPaperFileName,
  setCoAuthors,
  setAccompanyingPersons,
  setRegisterForFocusedSessions,
  setSelectedFocusedSessions,
  setFocusedSessionFees,
  fetchFocusedSessionFee,
  navigationDecisionRef,
  setShowPaperSubmissionSummary,
  isPendingPaperApprovalStatus,
  isApprovalMandateEnabled,
  abstractStepNumber,
  setResumePostPaymentAbstract,
  setIsInitialLoad,
  setNavigationDecisionVersion,
}) => {
  const lastAutoCheckKeyRef = useRef(null);
  const checkExistingRegistrationRef = useRef(null);

  const checkExistingRegistration = useCallback(
    async (email, conferenceNameParam) => {
      if (!email) {
        return;
      }

      const requestId = ++registrationCheckRequestIdRef.current;
      const isLatestRequest = () =>
        registrationCheckRequestIdRef.current === requestId;
      const safeSetStep = (nextStep) => {
        if (!isLatestRequest()) {
          console.log(
            "⏭️ Skipping stale step update from older registration check:",
            nextStep,
          );
          return;
        }
        setStep(nextStep);
      };

      setHasPaperSubmitted(false);
      setPaperApprovalStatus(null);
      setSubmittedPapers([]);
      setResumePostPaymentAbstract?.(false);

      try {
        const registrations = await fetchUserRegistrations({
          registrationBaseUrl,
          email,
          conferenceName: conferenceNameParam,
        });

        if (registrations.length > 0) {
          const existingReg = registrations.find(
            (reg) => reg.conference === conferenceNameParam && !reg.cancelled,
          );

          if (existingReg && existingReg.user) {
            const isPaid =
              existingReg.paid === true ||
              hasSuccessfulPaymentRecord(existingReg.payment) ||
              existingReg.confirmationDate !== null;

            setFormData((prev) => {
              conferenceDataLoadedRef.current = true;

              return {
                ...prev,
                email: existingReg.user.email,
                title: existingReg.user.title || "",
                firstName: existingReg.user.firstName || "",
                lastName: existingReg.user.lastName || "",
                mobile: existingReg.user.phone || "",
                gender: existingReg.user.gender || "",
                country: existingReg.user.country || "",
                state: existingReg.user.state || "",
                city: existingReg.user.city || "",
                address: existingReg.user.address || "",
                pincode: existingReg.user.zipCode || "",
                affiliation: existingReg.affiliation || "",
                institution: existingReg.institution || "",
                conference: existingReg.conference,
                category: existingReg.category || "",
                categoryId: existingReg.categoryId || null,
                registrationType: existingReg.registrationType || "",
                registrationTypeId: existingReg.registrationTypeId || null,
                membershipNumber: existingReg.membershipNumber || "",
                isaMembershipNumber: existingReg.isaMembershipNumber || "",
                department: existingReg.department || "",
                departmentId: existingReg.departmentId || null,
                otherDepartment: existingReg.otherDepartment || "",
                studentIdCard: existingReg.studentIdURL
                  ? existingReg.studentIdURL
                  : null,
                studentIdCardUploaded: existingReg.studentIdURL ? true : false,
                mciRegistered: existingReg.mciRegistered || "No",
                mciNumber: existingReg.mciNumber || "",
                specialRequirements: existingReg.specialRequirements || "",
                dietaryRestrictions:
                  existingReg.additionalData?.dietaryRestrictions || "",
                specialization:
                  existingReg.additionalData?.specialization ||
                  existingReg.additionalData?.specilization ||
                  "",
                designation: existingReg.additionalData?.designation || "",
                // selectedWorkshopValue: existingReg.additionalData?.selectedWorkshopValue || "",

                additionalData: {
                  ...(prev.additionalData || {}),
                  selectedWorkshopValue:
                    existingReg.additionalData?.selectedWorkshopValue || "",
                },

                paid: isPaid,
                id: existingReg.registrationId,
                regId: existingReg.id,
                additionalFees:
                  existingReg.additionalFees &&
                  Array.isArray(existingReg.additionalFees)
                    ? existingReg.additionalFees
                    : [],
                zeroPayments:
                  existingReg.zeroPayments &&
                  Array.isArray(existingReg.zeroPayments)
                    ? existingReg.zeroPayments
                    : [],
                payment:
                  existingReg.payment && Array.isArray(existingReg.payment)
                    ? existingReg.payment
                    : [],
              };
            });

            setCategoriesHistory(
              Array.isArray(existingReg.additionalData?.categoriesHistory)
                ? existingReg.additionalData.categoriesHistory
                : [],
            );
            setRegistrationTypesHistory(
              Array.isArray(
                existingReg.additionalData?.registrationTypesHistory,
              )
                ? existingReg.additionalData.registrationTypesHistory
                : [],
            );
            if (isPaid) {
              setInitialPaidCategoryId(existingReg.categoryId || null);
              setInitialPaidRegistrationTypeId(
                existingReg.registrationTypeId || null,
              );
              setInitialPaidCategoryValue(existingReg.category || "");
              setInitialPaidRegistrationTypeValue(
                existingReg.registrationType || "",
              );
            } else {
              setInitialPaidCategoryId(null);
              setInitialPaidRegistrationTypeId(null);
              setInitialPaidCategoryValue("");
              setInitialPaidRegistrationTypeValue("");
            }

            let hasPaperSubmitted = false;
            let loadedPaperStatus = null;
            const savedWantsToSubmitAbstract =
              existingReg.additionalData?.wantsToSubmitAbstract === true ||
              existingReg.additionalData?.wantsToSubmitAbstract === "true";

            try {
              const candidateRegistrationIds = [
                existingReg.id,
                existingReg.registrationId,
              ].filter(
                (value, index, self) =>
                  value != null && self.indexOf(value) === index,
              );

              let papers = [];
              for (const candidateId of candidateRegistrationIds) {
                const result = await fetchPapersByRegistrationId({
                  registrationBaseUrl,
                  registrationId: candidateId,
                });

                if (Array.isArray(result) && result.length > 0) {
                  papers = result;
                  break;
                }
              }

              if (papers && papers.length > 0) {
                hasPaperSubmitted = true;
                setHasPaperSubmitted(true);
                setSubmittedPapers(papers);
                const editablePaperStatuses = [
                  "DRAFT",
                  "MINOR_REVISION",
                  "MAJOR_REVISION",
                  "REVISION_REQUESTED",
                ];
                const paper =
                  papers.find((candidatePaper) =>
                    editablePaperStatuses.includes(
                      (candidatePaper.status || "").trim().toUpperCase(),
                    ),
                  ) || papers[0];
                setExistingPaperId(paper.id);

                const lockedStatuses = [
                  "SUBMITTED",
                  "UNDER_REVIEW",
                  "ACCEPTED",
                  "REJECTED",
                  "REVIEWED",
                ];
                const isLocked = lockedStatuses.includes(paper.status);
                setIsPaperLocked(isLocked);
                setPaperApprovalStatus(paper.status || null);
                loadedPaperStatus = paper.status || null;
                setPaperTitle(paper.title || "");
                setAbstractText(paper.abstractText || "");
                setPresentationMode(paper.presentationMode || "");
                setSelectedTrack(paper.trackId || null);
                setWantsToSubmitAbstract(true);

                let hasFile = false;
                let actualFileName = "uploaded-paper.pdf";
                const paperRegistrationId =
                  paper.registrationId ||
                  existingReg.id ||
                  existingReg.registrationId;
                try {
                  const fileMetadata = await fetchPaperFileMetadata({
                    registrationBaseUrl,
                    registrationId: paperRegistrationId,
                  });
                  hasFile = fileMetadata.exists;
                  actualFileName = fileMetadata.fileName;
                } catch (error) {
                  console.log("📎 Error checking file:", error.message);
                  hasFile = false;
                }

                if (hasFile) {
                  setPaperFileUploaded(true);
                  setPaperFileName(actualFileName);
                }

                const hasAbstract =
                  paper.abstractText && paper.abstractText.trim().length > 0;
                if (hasAbstract && hasFile) {
                  setSubmissionMethod("BOTH");
                } else if (hasAbstract) {
                  setSubmissionMethod("TEXTAREA");
                } else if (hasFile) {
                  setSubmissionMethod("FILE");
                } else {
                  setSubmissionMethod("TEXTAREA");
                }

                try {
                  const coAuthorsData = await fetchCoAuthorsByPaperId({
                    registrationBaseUrl,
                    paperId: paper.id,
                  });
                  if (coAuthorsData && coAuthorsData.length > 0) {
                    setCoAuthors(
                      coAuthorsData.map((coAuthor) => ({
                        name: coAuthor.name || "",
                        email: coAuthor.email || "",
                        phone: coAuthor.phone || "",
                        specialization: coAuthor.specialization || "",
                        designation: coAuthor.designation || "",
                      })),
                    );
                  }
                } catch (error) {
                  console.error("Error loading co-authors:", error);
                }
              }

              if (!hasPaperSubmitted && email && conferenceNameParam) {
                try {
                  const parsedCount = await fetchPaperCount({
                    registrationBaseUrl,
                    email,
                    conference: conferenceNameParam,
                  });
                  if (parsedCount > 0) {
                    hasPaperSubmitted = true;
                    setHasPaperSubmitted(true);
                    setSubmittedPapers(
                      Array.from({ length: parsedCount }, (_, index) => ({
                        id: `count-placeholder-${index}`,
                      })),
                    );
                    setWantsToSubmitAbstract(true);
                    if (!loadedPaperStatus) {
                      loadedPaperStatus = "DRAFT";
                      setPaperApprovalStatus("DRAFT");
                    }
                  }
                } catch (fallbackError) {
                  console.error(
                    "Error in paper count fallback lookup:",
                    fallbackError,
                  );
                }
              }

              if (!hasPaperSubmitted && savedWantsToSubmitAbstract) {
                hasPaperSubmitted = true;
                setHasPaperSubmitted(true);
                setSubmittedPapers([]);
                setWantsToSubmitAbstract(true);
                if (!loadedPaperStatus) {
                  loadedPaperStatus = "DRAFT";
                  setPaperApprovalStatus("DRAFT");
                }
              }
            } catch (error) {
              console.error("Error loading paper data:", error);
            }

            const storedAdditionalFees =
              existingReg.additionalFees &&
              Array.isArray(existingReg.additionalFees)
                ? existingReg.additionalFees
                : [];
            const storedZeroPayments =
              existingReg.zeroPayments &&
              Array.isArray(existingReg.zeroPayments)
                ? existingReg.zeroPayments
                : [];

            if (storedAdditionalFees.length > 0 && accompanyingPersonFee) {
              const accompanyingPersonsFromAdditionalFees = storedAdditionalFees
                .filter((fee) => fee.feeTypeId === accompanyingPersonFee.id)
                .map((fee) => ({
                  name: fee.name,
                  paymentId: fee.paymentId,
                  paid: !!fee.paymentId,
                  feeTypeId: fee.feeTypeId,
                  id: fee.id,
                }));

              const accompanyingPersonsFromZeroPayments = storedZeroPayments
                .filter(
                  (zeroPayment) =>
                    zeroPayment.feeTypeId === accompanyingPersonFee.id,
                )
                .map((zeroPayment) => ({
                  name: zeroPayment.name,
                  paymentId: null,
                  paid: true,
                  feeTypeId: zeroPayment.feeTypeId,
                  id: zeroPayment.id,
                }));

              const accompanyingPersonsList = [
                ...accompanyingPersonsFromAdditionalFees,
                ...accompanyingPersonsFromZeroPayments,
              ];

              if (accompanyingPersonsList.length > 0) {
                setAccompanyingPersons(accompanyingPersonsList);
              }

              const focusedSessionsFromAdditionalFees = storedAdditionalFees
                .filter(
                  (fee) =>
                    fee.feeTypeId !== accompanyingPersonFee.id &&
                    !fee.paymentId,
                )
                .map((fee) => fee.feeTypeId);

              const focusedSessionsFromZeroPayments = storedZeroPayments
                .filter(
                  (zeroPayment) =>
                    zeroPayment.feeTypeId !== accompanyingPersonFee.id,
                )
                .map((zeroPayment) => zeroPayment.feeTypeId);

              const focusedSessionsList = [
                ...new Set([
                  ...focusedSessionsFromAdditionalFees,
                  ...focusedSessionsFromZeroPayments,
                ]),
              ];

              if (focusedSessionsList.length > 0) {
                setRegisterForFocusedSessions("Yes");
                setSelectedFocusedSessions(focusedSessionsList);

                if (conferenceId) {
                  focusedSessionsList.forEach((sessionId) => {
                    fetchFocusedSessionFee(sessionId).then((feeData) => {
                      if (feeData) {
                        setFocusedSessionFees((prevFees) => ({
                          ...prevFees,
                          [sessionId]: feeData,
                        }));
                      }
                    });
                  });
                }
              }
            }

            const isPaperPendingApproval =
              isApprovalMandateEnabled &&
              hasPaperSubmitted &&
              (isPendingPaperApprovalStatus(loadedPaperStatus) ||
                !loadedPaperStatus);

            navigationDecisionRef.current = {
              isPaid,
              hasPaperSubmitted,
              paperStatus: loadedPaperStatus,
              ready: true,
            };
            setNavigationDecisionVersion?.((version) => version + 1);

            if (isPaperPendingApproval) {
              navigationDecisionRef.current.ready = false;
              setShowPaperSubmissionSummary(true);
              safeSetStep(3);
              setIsInitialLoad?.(false);
              return;
            }

            const normalizedLoadedPaperStatus = (loadedPaperStatus || "")
              .trim()
              .toUpperCase();
            const canResumeLoadedPaper =
              normalizedLoadedPaperStatus === "DRAFT" ||
              normalizedLoadedPaperStatus === "MINOR_REVISION" ||
              normalizedLoadedPaperStatus === "MAJOR_REVISION" ||
              normalizedLoadedPaperStatus === "REVISION_REQUESTED";
            if (canResumeLoadedPaper) {
              setResumePostPaymentAbstract?.(true);
            }
            const shouldOpenPostPaymentPaperStep =
              isPostPaymentAbstractEnabled(abstractStepNumber) &&
              ((isPaid && !hasPaperSubmitted) || canResumeLoadedPaper);

            if (shouldOpenPostPaymentPaperStep) {
              navigationDecisionRef.current.ready = false;
              setShowPaperSubmissionSummary(false);
              safeSetStep(POST_PAYMENT_ABSTRACT_FORM_STEP);
              setIsInitialLoad?.(false);
              return;
            }

            // Let useRegistrationNavigation route after abstract metadata has loaded.
            // Routing here can consume the decision while abstractStepNumber is still 0.
          } else if (registrations.length > 0) {
            const anyRegistration = registrations[0];
            if (anyRegistration && anyRegistration.user) {
              setFormData((prev) => ({
                ...prev,
                email: prev.email || anyRegistration.user.email,
                firstName:
                  prev.firstName || anyRegistration.user.firstName || "",
                lastName: prev.lastName || anyRegistration.user.lastName || "",
                mobile: prev.mobile || anyRegistration.user.phone || "",
                country: prev.country || anyRegistration.user.country || "",
                state: prev.state || anyRegistration.user.state || "",
                city: prev.city || anyRegistration.user.city || "",
                address: prev.address || anyRegistration.user.address || "",
                pincode: prev.pincode || anyRegistration.user.zipCode || "",
                affiliation:
                  prev.affiliation || anyRegistration.affiliation || "",
                institution:
                  prev.institution || anyRegistration.institution || "",
                id: null,
                regId: null,
                conference: conferenceNameParam,
                category: "",
                categoryId: null,
                registrationType: "",
                registrationTypeId: null,
              }));
            }

            safeSetStep(5);
            setIsInitialLoad?.(false);
          } else {
            setFormData((prev) => ({
              ...prev,
              id: null,
              regId: null,
              conference: conferenceNameParam,
              category: "",
              categoryId: null,
              registrationType: "",
              registrationTypeId: null,
            }));

            safeSetStep(1);
            setIsInitialLoad?.(false);
          }
        } else {
          safeSetStep(5);
          setIsInitialLoad?.(false);
        }
      } catch (error) {
        console.error("Error checking existing registration:", error);
        safeSetStep(5);
        setIsInitialLoad?.(false);
      }
    },
    [
      accompanyingPersonFee,
      conferenceDataLoadedRef,
      conferenceId,
      fetchFocusedSessionFee,
      isPendingPaperApprovalStatus,
      isApprovalMandateEnabled,
      abstractStepNumber,
      setResumePostPaymentAbstract,
      navigationDecisionRef,
      registrationBaseUrl,
      registrationCheckRequestIdRef,
      setAbstractText,
      setAccompanyingPersons,
      setCategoriesHistory,
      setCoAuthors,
      setExistingPaperId,
      setFocusedSessionFees,
      setFormData,
      setHasPaperSubmitted,
      setInitialPaidCategoryId,
      setInitialPaidCategoryValue,
      setInitialPaidRegistrationTypeId,
      setInitialPaidRegistrationTypeValue,
      setIsPaperLocked,
      setIsInitialLoad,
      setNavigationDecisionVersion,
      setPaperApprovalStatus,
      setPaperFileName,
      setPaperFileUploaded,
      setPaperTitle,
      setPresentationMode,
      setSubmissionMethod,
      setRegisterForFocusedSessions,
      setRegistrationTypesHistory,
      setSelectedFocusedSessions,
      setSelectedTrack,
      setSubmittedPapers,
      setShowPaperSubmissionSummary,
      setStep,
      setWantsToSubmitAbstract,
    ],
  );

  useEffect(() => {
    checkExistingRegistrationRef.current = checkExistingRegistration;
  }, [checkExistingRegistration]);

  useEffect(() => {
    const selectedConferenceName = conferenceName || existingUser?.conference;
    if (!existingUser || !existingUser.email || !selectedConferenceName) {
      return;
    }

    const autoCheckKey = `${existingUser.email}::${selectedConferenceName}::${isApprovalMandateEnabled}::${abstractStepNumber}`;
    if (lastAutoCheckKeyRef.current === autoCheckKey) {
      return;
    }

    lastAutoCheckKeyRef.current = autoCheckKey;
    checkExistingRegistrationRef.current?.(
      existingUser.email,
      selectedConferenceName,
    );
  }, [
    existingUser?.conference,
    existingUser?.email,
    conferenceName,
    isApprovalMandateEnabled,
    abstractStepNumber,
  ]);

  return { checkExistingRegistration };
};
